import { defineStore } from "pinia";
import * as authApi from "@/api/auth";
import router from "@/router";
import { generateSignedUrl } from '@/api/storage';
import { mixpanelService } from "@/services/mixpanel";
import { StreamChat } from 'stream-chat';
import { notifyError } from "@/utils/notification";
import { isSuspendedMemberError, SUSPENDED_MEMBER_MESSAGE } from "@/utils/subscriptionGuards";
import { useSubscriptionStore } from "@/stores/subscriptionStore";

const chatApiKey = process.env.VUE_APP_STREAM_API_KEY || 'r3qwrczd357a';
export const chatClient = StreamChat.getInstance(chatApiKey);

export const REMEMBER_ME_KEY = "rememberMe";
const SESSION_COOKIE_KEY = "ilearnd_browser_session";
// Default persistedstate localStorage key = the store id
const PERSISTED_AUTH_KEY = "auth";

// Save the "Remember me" choice and (re)start the browser-session marker cookie
export const setRememberMe = (value) => {
  localStorage.setItem(REMEMBER_ME_KEY, value ? "true" : "false");
  // No expiry -> session cookie: dropped on browser close, kept across tab closes
  document.cookie = `${SESSION_COOKIE_KEY}=1; path=/; SameSite=Lax`;
};

// On boot: if not remembered and the marker cookie is gone, clear the saved login
export const enforceSessionLifetime = () => {
  const remembered = localStorage.getItem(REMEMBER_ME_KEY) === "true";
  const hasBrowserSession = document.cookie
    .split("; ")
    .some((c) => c.startsWith(`${SESSION_COOKIE_KEY}=`));
  if (!remembered && !hasBrowserSession) {
    localStorage.removeItem(PERSISTED_AUTH_KEY);
  }
};

export const useAuthStore = defineStore("auth", {
  persist: true,
  state: () => ({
    token: null,
    refreshToken: null,
    chatToken: null,
    user: null,
    userType: null,
    profileImageUrl: '',
    imageCache: null
  }),
  actions: {
    async googleAuthLogin() {
      return await authApi.googleLogin();
    },
    async googleAuthSignup(role) {
      return await authApi.googleSignup(role);
    },
    async connectToChat() {
      if (!this.user) return;
      if (!chatClient.user) {
        try {
          await chatClient.connectUser(
            {
              id: this.user.userId,
              name: this.user.username,
              image: this.profileImageUrl,
              privacy_settings: {
                typing_indicators: {
                  enabled: true,
                },
                read_receipts: {
                  enabled: true,
                },
              },
            },
            this.chatToken
          );
          console.log("Stream Chat Connected");
        } catch (error) {
          // console.error("Failed to connect to Stream Chat:", error);
        }
      }
    },

    async login(username, password, ignoreVerification = false) {
      try {
        const res = await authApi.login(username, password);
        const data = res.data.data;
        const TwoFAOnLogin = data.user.twoFactorEnabled;
        const chatToken = data.chatToken;

        this.setLoginData(data.accessToken, data.refreshToken, chatToken, data.user);

        if (!TwoFAOnLogin) {
          mixpanelService.track("User Logged In", {
            method: "Email",
            $email: data.user.email,
            $name: data.user.username,
            role: data.user.role,
          });
        }

        if (TwoFAOnLogin && !ignoreVerification) {
          router.push("/verify");
          return
        }
        await this.loadProfileImage();
        await this.connectToChat();
        if (!TwoFAOnLogin) {
          if (data.user.role === "super_admin" || data.user.role === "admin") {
            router.push("/admin_dashboard");
          } else {
            this.navigateBasedOnRole(data.user.role, data.user.registrationCompleted);
          }
        }
      } catch (err) {
        console.error("Login failed:", err.response?.data || err.message);
        throw err;
      }
    },
    async handleOAuthData(data) {
      // Google login/signup is always remembered (persisted in localStorage)
      setRememberMe(true);
      const TwoFAOnLogin = data.user.twoFactorEnabled;

      if (data.user.isNewUser) {
        const userId = data.user.id || data.user._id || data.user.userId;

        if (userId) {
          mixpanelService.alias(userId);

          mixpanelService.track("User Signed Up", {
            role: data.user.role,
            method: "Google",
          });

          mixpanelService.identify(userId, {
            $email: data.user.email,
            $name: data.user.username,
            role: data.user.role,
            $created: new Date().toISOString(),
            registration_completed: data.user.registrationCompleted || false,
          });
        }
      }

      this.setLoginData(data.accessToken, data.refreshToken, data.chatToken, data.user);

      if (!TwoFAOnLogin) {
        mixpanelService.track("User Logged In", {
          method: "Google",
          $email: data.user.email,
          $name: data.user.username,
          role: data.user.role,
        });
      }

      if (TwoFAOnLogin) {
        router.push("/verify");
        return
      }
      if (data.user.role === "super_admin" || data.user.role === "admin") {
        router.push("/admin_dashboard");
      } else {
        this.navigateBasedOnRole(data.user.role, data.user.registrationCompleted);
      }
    },
    async refresh() {
      try {
        const res = await authApi.refreshToken(this.refreshToken);
        const data = res.data.data;
        const chatToken = data.chatToken;
        this.setLoginData(data.accessToken, data.refreshToken, chatToken, data.user);
      } catch (err) {
        console.error("Token refresh failed", err);
        localStorage.setItem("TOKEN_CHECKED",true);
        // Suspended institute member error
        if (isSuspendedMemberError(err)) {
          notifyError("Access unavailable", err.response?.data?.message || SUSPENDED_MEMBER_MESSAGE);
        }
        this.logout();
      }
    },

    async register(payload) {
      try {
        // return await authApi.register(payload);
        const response = await authApi.register(payload);
        const newUser = response.data.user || response.data.data?.user || response.data.data;
        const userId = newUser?.id || newUser?._id || newUser?.userId;

        if (userId) {
          mixpanelService.alias(userId);

          mixpanelService.track("User Signed Up", {
            role: payload.role.name,
            method: "Email",
          });

          mixpanelService.identify(userId, {
            $email: payload.email,
            $name: payload.username,
            role: payload.role.name,
            $created: new Date().toISOString(),
            registration_completed: newUser.registrationCompleted || false,
          });
        }

        return response;
      } catch (err) {
        console.error("Registration failed", err.response?.data || err.message);
        throw err;
      }
    },

    async resetPassword(email) {
      try {
        return await authApi.resetPassword(email);
      } catch (err) {
        console.error("Password reset failed", err.response?.data || err.message);
        throw err;
      }
    },

    async findKeycloackRole(role) {
      try {
        let roles = await authApi.fetchRoles(role);
        roles = roles.data;
        const matched = roles.find(r => r.name === role);
        if (!matched) {
          return null;
        }
        return {
          id: matched.id,
          name: matched.name
        };
      } catch (err) {
        console.error("Password reset failed", err.response?.data || err.message);
        throw err;
      }
    },

    updateLocalUser(user) {
      this.user = user;
    },
    async logout(redirect = true) {
      mixpanelService.reset();
      await chatClient.disconnectUser();
      useSubscriptionStore().reset();

      this.token = null;
      this.refreshToken = null;
      this.user = null;
      this.userType = null;
      this.profileImageUrl = '';
      this.imageCache = null;
      if (redirect) router.push("/");
    },

    navigateBasedOnRole(role, profileComplete) {
      const redirectRequest = localStorage.getItem('redirectAfterLogin');
      if (redirectRequest) {
        localStorage.removeItem('redirectAfterLogin');
        router.push(redirectRequest);
        return;
      }
      switch (role) {
        case "student":
          if (profileComplete) router.push("/student_dashboard");
          else router.push("/studentOnBoarding");
          break;
        case "tutor":
          if (profileComplete && this.user?.verificationRequested) router.push("/tutor_dashboard");
          else router.push("/tutorOnBoarding");
          break;
        case "institute":
          if (profileComplete) router.push("/ourTutors");
          else router.push("/institutionOnBoarding");
          break;
        default:
          break;
      }
    },
    setUserType(type) {
      this.userType = type;
    },
    setLoginData(token, refreshToken, chatToken, user) {
      this.token = token;
      this.refreshToken = refreshToken;
      this.chatToken = chatToken;

      const uniqueId = user.userId || user.id || user._id;

      if (user && uniqueId) {
        mixpanelService.identify(uniqueId, {
          $email: user.email,
          $name: user.username,
          role: user.role,
          account_type: "Standard",
          registration_completed: user.registrationCompleted || false,
        });
      }

      this.user = user;
      this.userType = user.role;
      localStorage.removeItem("TOKEN_CHECKED");
    },

    async completeOnboarding() {
      if (this.user) {
        this.user.registrationCompleted = true;
        const uniqueId = this.user.userId || this.user.id || this.user._id;

        if (uniqueId) {
          mixpanelService.identify(uniqueId, {
            registration_completed: this.user.registrationCompleted || true,
          });
        }
      }
    },

    async loadProfileImage() {
      if (!this.user) return;

      const filePath = (this.user.role === 'student' || this.user.role === 'institute')
        ? this.user.profilePhotoURL
        : this.user.professionalPhotoURL;

      if (!filePath) return;

      // Return cached URL if available
      if (this.imageCache?.filePath === filePath) {
        this.profileImageUrl = this.imageCache.url;
        return;
      }

      // Prevent reloading if already loading or loaded
      if (this.profileImageUrl && this.profileImageUrl !== '') {
        return;
      }

      try {
        const url = await generateSignedUrl(filePath);
        this.profileImageUrl = url;
        this.imageCache = { filePath, url };
      } catch (error) {
        console.error("Error loading profile image:", error);
        this.profileImageUrl = '';
      }
    }

  },

});
